import React, {useEffect, useMemo, useState} from 'react';
import dayjs from 'dayjs';
import {useTranslation} from 'react-i18next';
import {Space} from 'antd';
import {useDispatch, useSelector} from 'react-redux';
import PropTypes from 'prop-types';
import {StyledExpenses, StyledExpensesMoreButton, StyledExpensesContainer, StyledExpensesEmpty} from './StyledExpenses';
import {getExpensesTabColumns} from '../../ExpensesPage/ExpensesTab/ExpensesTabColumns';
import RightSideDrawer from '../../../RightSideDrawer';
import Spin from '../../../Spin';
import Table from '../../../Table';
import AuthenticationWindow from '../../CardsPage/AuthenticationWindow';
import AddExpenseModal from '../../ExpensesPage/AddExpenseModal';
import ExpenseAgentChat from '../AgentChatPanel/AgentChatPanel';
import ExpenseDetailsPanel from '../../ExpensesPage/ExpenseDetailsPanel';
import ApproveExpenseModal from '../../ExpensesPage/ExpenseDetailsPanelTabs/tabComponents/ApproveExpenseModal';
import RejectExpenseModal from '../../ExpensesPage/ExpenseDetailsPanelTabs/tabComponents/RejectExpenseModal';
import ExpensesWebsocketConnection from '../../ExpensesPage/ExpensesWebsocketConnection';
import {ExpensesIcon} from '../../../../icons';
import {expenseActions} from '../../../../state/actions/expense.actions';
import {invoicesActions} from '../../../../state/actions/invoice.actions';
import {
  backendWebsocketActionsConstants,
  DEFAULT_MODAL_PROPS,
  expenseReviewStatusConstants,
  SCAActionsConstants
} from '../../../../constants';
import {useExpenseRelatedData, useIsCompanyChanged} from '../../../../hooks';
import {expensesHelpers, objectHelpers, reviewHelpers, transactionsHelpers} from '../../../../utils/helpers';
import {ReactComponent as EmptySvgImage} from '../../../../static/images/pages/overview/empty-expenses.svg';
import overviewShared from '../OverviewShared/overviewShared';


const tableKeys = {
  NEED_YOUR_APPROVAL: 'need-your-approval',
  NEED_YOUR_INPUT: 'need-your-input',
  LAST_10_EXPENSES: 'last-10-expenses',
}

const { NEED_YOUR_APPROVAL, NEED_YOUR_INPUT, LAST_10_EXPENSES } = tableKeys;

const defaultModalProps = {
  ...DEFAULT_MODAL_PROPS,
  tableKey: undefined
}

const dateFormat = 'YYYY-MM-DD';

const Expenses = ({
  handleOpenDetails,
  ...rest
}) => {
  const [t] = useTranslation(['main', 'subscriptions']);
  const [approvalTableProps, setApprovalTableProps] = useState(overviewShared.defaultTableProps);
  const [inputTableProps, setInputTableProps] = useState(overviewShared.defaultTableProps);
  const [lastExpensesTableProps, setLastExpensesTableProps] = useState(overviewShared.defaultTableProps);

  const [detailsWindowProps, setDetailsWindowProps] = useState(defaultModalProps);
  const [approveModalProps, setApproveModalProps] = useState(DEFAULT_MODAL_PROPS);
  const [rejectModalProps, setRejectModalProps] = useState(DEFAULT_MODAL_PROPS);

  const companyId = useIsCompanyChanged();

  const dispatch = useDispatch();

  const {
    subscriptions,
    transactions,
    setSubscriptions,
    loadExpenseRelatedData,
    loadExpenseTransactions,
    authWindowProps,
    handleCloseAuthModal,
  } = useExpenseRelatedData();

  const { isAdmin, isOpenAddExpenseModal, employeeEmail } = useSelector((state) => ({
    isAdmin: state.user?.isAdmin,
    isOpenAddExpenseModal: state?.expense.addExpenseModalProps.open,
    employeeEmail: state.user?.employee?.email,
  }));

  const detailsWindowSubscription = useMemo(() => {
    return expensesHelpers.getExpenseRelatedObject({detailsWindowProps, data: subscriptions, key: 'subscription_id'});
  }, [detailsWindowProps?.expense?.subscription_id, detailsWindowProps?.open, subscriptions]); // eslint-disable-line react-hooks/exhaustive-deps

  const detailsWindowTransaction = useMemo(() => {
    return expensesHelpers.getExpenseRelatedObject({detailsWindowProps, data: transactions, key: 'card_transaction_id'});
  }, [detailsWindowProps?.expense?.card_transaction_id, detailsWindowProps?.open, transactions]); // eslint-disable-line react-hooks/exhaustive-deps

  const isEmpty = useMemo(() => {
    const noData = !approvalTableProps.data.length && !inputTableProps.data.length && !lastExpensesTableProps.data.length;
    const notLoading = !approveModalProps.loading && !inputTableProps.loading && !lastExpensesTableProps.loading;
    return noData && notLoading;
  }, [approvalTableProps, inputTableProps, lastExpensesTableProps]); // eslint-disable-line react-hooks/exhaustive-deps

  const isEnableLoading = useMemo(() => {
    const noData = !approvalTableProps.data.length && !inputTableProps.data.length && !lastExpensesTableProps.data.length;
    const loading = approvalTableProps.loading || inputTableProps.loading || lastExpensesTableProps.loading;
    return noData && loading;
  }, [approvalTableProps, inputTableProps, lastExpensesTableProps]); // eslint-disable-line react-hooks/exhaustive-deps

  const getQueryParams = (key) => ({
    [NEED_YOUR_APPROVAL]: {
      approver_email: [employeeEmail, 'any_admin'],
      per_page: 3,
      review_status: expenseReviewStatusConstants.IN_REVIEW
    },
    [NEED_YOUR_INPUT]: {
      per_page: 3,
      reported_by: employeeEmail,
      missing_mandatory_fields: true,
      start_date: dayjs().subtract(100, 'day').format(dateFormat),
      end_date: dayjs().format(dateFormat)
    },
    [LAST_10_EXPENSES]: {
      per_page: 10,
      reported_by: isAdmin ? undefined : employeeEmail
    },
  }[key] || {});

  const getInitialQueryParams = (key) => ({
    [NEED_YOUR_APPROVAL]: { per_page: Math.max(approvalTableProps.data.length, 3) },
    [NEED_YOUR_INPUT]: { per_page: Math.max(inputTableProps.data.length, 3) },
  }[key] || {});

  const loadTableData = ({key, setter, query, initial = false} = {}) => {
    setter?.(prevState => ({...prevState, loading: true}));

    // Ensure order_by is always set for expenses lists
    const q = { ...getQueryParams(key), ...query, order_by: query?.order_by || '-date' };

    dispatch(expenseActions.getExpenses({
      query: q,
      successCallback: (data) => {
        const results = data?.results || [];
        setter?.(prevState => ({
          ...prevState,
          data: initial ? results : [...prevState.data, ...results],
          loading: false,
          pagination: data.pagination
        }));
      },
      errorCallback: () => {
        setter?.(prevState => ({
          ...prevState,
          loading: false,
          pagination: undefined
        }));
      }
    }));
  }

  const getTableState = (key) => ({
    [LAST_10_EXPENSES]: {state: lastExpensesTableProps, setState: setLastExpensesTableProps},
    [NEED_YOUR_APPROVAL]: {state: approvalTableProps, setState: setApprovalTableProps},
    [NEED_YOUR_INPUT]: {state: inputTableProps, setState: setInputTableProps},
  }[key] || undefined);

  const updateTableData = ({tableKey, initial = false, query = {}} = {}) => {
    const props = {
      [LAST_10_EXPENSES]: {key: LAST_10_EXPENSES, setter: setLastExpensesTableProps},
      [NEED_YOUR_APPROVAL]: {key: NEED_YOUR_APPROVAL, setter: setApprovalTableProps},
      [NEED_YOUR_INPUT]: {key: NEED_YOUR_INPUT, setter: setInputTableProps},
    }[tableKey] || undefined;
    if (!props) return;

    loadTableData({...props, query: initial ? { cursor: undefined, ...query } : query, initial});
  }

  useEffect(() => {
    if (!companyId) return;

    loadTableData({key: LAST_10_EXPENSES, setter: setLastExpensesTableProps, initial: true});
    loadTableData({key: NEED_YOUR_INPUT, setter: setInputTableProps, initial: true});
    if (isAdmin) {
      loadTableData({key: NEED_YOUR_APPROVAL, setter: setApprovalTableProps, initial: true});
    } else {
      setApprovalTableProps({...approvalTableProps, loading: false});
    }
  }, [companyId]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleLoadMore = (key, cursor) => {
    const setter = {
      [NEED_YOUR_APPROVAL]: setApprovalTableProps,
      [NEED_YOUR_INPUT]: setInputTableProps,
    }[key] || undefined
    if (!setter) return;

    loadTableData({key, setter, query: { cursor }})
  }

  const getLabel = (key) => (
    <Space size='small' align='center'>
      <ExpensesIcon /><span>{t(`overviewPage.expenses.${key}`)}</span>
    </Space>
  );

  const handleRowClick = (expense, tableKey) => {
    if (detailsWindowProps?.expense?.id === expense.id) return;

    setDetailsWindowProps({
      ...defaultModalProps,
      index: `${expense.id}_${tableKey}`,
      open: true,
      expense,
      tableKey,
    });

    loadExpenseRelatedData(expense);

    handleOpenDetails?.(true);
  }

  const handleApproveTableExpense = ({e, expense, data, tableKey} = {}) => {
    e?.stopPropagation();

    const canQuickApprove = reviewHelpers.canQuickApprove({data: expense, employeeEmail});
    if (canQuickApprove) {
      handleApproveExpense({e, expense, data, tableKey});
    } else {
      setApproveModalProps({
        ...DEFAULT_MODAL_PROPS,
        open: true,
        expense,
        tableKey
      });
    }
  }

  const handleOpenRejectModal = ({e, expense, data = {}, tableKey} = {}) => {
    e?.stopPropagation();
    setRejectModalProps({
      ...DEFAULT_MODAL_PROPS,
      open: true,
      expense,
      data,
      tableKey
    });
  }

  const getTableColumns = (tableKey) => {
    return getExpensesTabColumns({
      t,
      isAdmin,
      employeeEmail,
      onApprove: (props) => handleApproveTableExpense({...props, tableKey}),
      onReject: (props) => handleOpenRejectModal({...props, tableKey}),
    }).map(c => ({...c, sorter: false}));
  }

  const getRowClassName = (record, key) => {
    return transactionsHelpers.getRowClassName({
      index: `${record.id}_${key}`,
      record,
      selectedRowIndex: detailsWindowProps?.index,
    });
  };

  const getTable = ({key, tableProps, showSeeMoreButton = true} = {}) => {
    const cursor = tableProps?.pagination?.cursor;
    const isAvailableSeeMore = showSeeMoreButton && cursor;
    return (
      <Space size='small' direction='vertical'>
        <Table
          columns={getTableColumns(key)}
          pagination={false}
          dataSource={tableProps?.data || []}
          onRow={(record) => ({
            onClick: () => handleRowClick(record, key)
          })}
          rowKey='id'
          rowClassName={(record) => getRowClassName(record, key)}
        />
        {isAvailableSeeMore && (
          <StyledExpensesMoreButton
            loading={tableProps?.loading}
            onClick={() => handleLoadMore(key, cursor)}
            type='link'
          >
            {t('see')} {t('more')}
          </StyledExpensesMoreButton>
        )}
      </Space>
    )
  }

  const handleCloseApproveModal = () => setApproveModalProps({...approveModalProps, open: false});

  const handleCloseRejectModal = () => setRejectModalProps({...rejectModalProps, open: false});

  const updateTables = (useCurrentDataLengthAsPerPage = false) => {
    const getAdditionalQuery = (tableProp) => {
      if (!useCurrentDataLengthAsPerPage) return {};
      return { per_page: Math.max(tableProp.data.length, 3) };
    }

    updateTableData({
      tableKey: LAST_10_EXPENSES,
      initial: true,
      query: getAdditionalQuery(lastExpensesTableProps)
    });

    updateTableData({
      tableKey: NEED_YOUR_INPUT,
      initial: true,
      query: {
        ...getInitialQueryParams(NEED_YOUR_INPUT),
        ...getAdditionalQuery(inputTableProps)
      }
    });

    if (isAdmin) {
      updateTableData({
        tableKey: NEED_YOUR_APPROVAL,
        initial: true,
        query: {
          ...getInitialQueryParams(NEED_YOUR_APPROVAL),
          ...getAdditionalQuery(approvalTableProps)
        }
      });
    }
  };

  const updateTableAndDetails = ({id, response} = {}) => {
    updateTables();

    if (detailsWindowProps.open && detailsWindowProps?.expense?.id === id) {
      setDetailsWindowProps(prevState => ({ ...prevState, expense: response }));
    }
  };

  const handleApproveDetailsExpense = ({e, entity, expense, data}) => {
    const action = data?.admin_override ? handleApproveTableExpense : handleApproveExpense;
    action({e, expense: expense || entity, data});
  }

  const handleApproveExpense = ({e, expense, data = {}, successCallback, errorCallback, tableKey}) => {
    e?.stopPropagation();

    const id = expense?.id;
    if (!id) return;

    dispatch(expenseActions.approveExpense({
      id,
      data,
      successCallback: (response) => {
        updateTableAndDetails({id, response, tableKey: tableKey || approveModalProps.tableKey || detailsWindowProps.tableKey});
        successCallback?.(response);
      },
      errorCallback: (response) => {
        expensesHelpers.handleExpenseError({response, fallbackKEy: 'approveExpenseError', t});
        errorCallback?.(response);
      },
    }));
  }

  const handleReviewEdit = () => {
    const id = detailsWindowProps?.expense?.id;
    if (!id) return;

    dispatch(expenseActions.reopenExpense({
      id,
      successCallback: (response) => updateTableAndDetails({id, response, tableKey: detailsWindowProps.tableKey}),
      errorCallback: (response) => expensesHelpers.handleExpenseError({response, fallbackKEy: 'reopenExpenseError', t}),
    }));
  }

  const handleReassignExpense = (e, data) => {
    const id = detailsWindowProps?.expense?.id;
    if (!id) return;

    dispatch(expenseActions.reassignExpense({
      id,
      data,
      successCallback: (response) => updateTableAndDetails({id, response, tableKey: detailsWindowProps.tableKey}),
      errorCallback: (response) => expensesHelpers.handleExpenseError({response, fallbackKey: 'reassignExpenseError', t}),
    }));
  }

  const handleDiscardAutoApproval = () => {
    const id = detailsWindowProps?.expense?.id;
    if (!id) return;

    dispatch(expenseActions.discardExpenseAutoApproval({
      id,
      successCallback: (response) => updateTableAndDetails({id, response, tableKey: detailsWindowProps.tableKey}),
      errorCallback: (response) => expensesHelpers.handleExpenseError({response, fallbackKey: 'discardAutoApprovalError', t}),
    }));
  }

  const handleOkRejectModal = ({fields, successCallback, errorCallback}) => {
    const id = rejectModalProps?.expense?.id;
    if (!id) return;

    setRejectModalProps(prevState => ({ ...prevState, loading: true }));

    dispatch(expenseActions.rejectExpense({
      id,
      data: {
        ...fields,
        ...(rejectModalProps?.data || {})
      },
      successCallback: (response) => {
        setRejectModalProps(defaultModalProps);
        updateTableAndDetails({id, response, tableKey: rejectModalProps.tableKey});
        successCallback?.(response);
      },
      errorCallback: (err) => {
        setRejectModalProps(prevState => ({ ...prevState, loading: false }));
        errorCallback?.(err?.response);
      },
    }));
  }

  const handleOkApproveModal = ({successCallback, errorCallback}) => {
    const expense = approveModalProps?.expense;
    if (!expense) return;

    setApproveModalProps(prevState => ({ ...prevState, loading: true }));
    handleApproveExpense({
      expense,
      data: { admin_override: true },
      successCallback: (response) => {
        successCallback?.(response);
        handleCloseApproveModal();
      },
      errorCallback: (response) => {
        setApproveModalProps(prevState => ({ ...prevState, loading: false }));
        errorCallback?.(response);
      }
    });
  }

  const handleEditExpense = (data) => {
    const id = detailsWindowProps?.expense?.id;
    if (!id) return;

    dispatch(expenseActions.updateExpense({
      id,
      data,
      successCallback: (response) => {
        const expenseId = detailsWindowProps?.expense?.id;
        if (detailsWindowProps.open && expenseId === id) {
          const isChangedReviewStatus = response?.review_status !== detailsWindowProps?.expense?.review_status;
          const isNeedYourInputTab = detailsWindowProps?.tableKey === NEED_YOUR_INPUT;

          if (isChangedReviewStatus || isNeedYourInputTab) {
            updateTables(isNeedYourInputTab);
            return;
          }

          setDetailsWindowProps(prevState => ({
            ...prevState,
            expense: { ...prevState.expense, ...data }
          }));

          const updateTableState = (tableProps, setTableProps) => {
            if (tableProps?.data.some(exp => exp.id === expenseId)) {
              setTableProps({
                ...tableProps,
                data: tableProps.data.map(exp => exp.id === expenseId ? { ...exp, ...data } : exp)
              });
            }
          };

          updateTableState(approvalTableProps, setApprovalTableProps);
          updateTableState(inputTableProps, setInputTableProps);
          updateTableState(lastExpensesTableProps, setLastExpensesTableProps);
        }
      },
      errorCallback: (response) => expensesHelpers.handleExpenseError({response, fallbackKey: 'updateExpenseError', t}),
    }));
  }

  const handleUpdateExpense = (updatedExpense) => {
    const selectedExpense = detailsWindowProps?.expense;
    const id = selectedExpense?.id;
    if (!id) return;

    if (updatedExpense) {
      const isChangedReviewStatus = updatedExpense?.review_status !== selectedExpense?.review_status;
      const isNeedYourInputTab = detailsWindowProps?.tableKey === NEED_YOUR_INPUT;

      if (detailsWindowProps.open && detailsWindowProps?.expense?.id === id) {
        setDetailsWindowProps(prevState => ({ ...prevState, expense: { ...prevState.expense, ...updatedExpense } }));
      }

      if (isChangedReviewStatus || isNeedYourInputTab) {
        updateTables(isNeedYourInputTab);
        return;
      }

      const tableKey = detailsWindowProps?.tableKey;
      const {setState} = getTableState(tableKey) || {};

      setState(prevState => ({
        ...prevState,
        data: prevState?.data.map(exp => exp.id === id ? {...exp, ...updatedExpense} : exp)
      }));
    }
  }

  const handleDeleteExpense = (deletedExpense) => {
    const id = deletedExpense?.id;

    const tableKeys = isAdmin
      ? [NEED_YOUR_INPUT, LAST_10_EXPENSES, NEED_YOUR_APPROVAL]
      : [NEED_YOUR_INPUT, LAST_10_EXPENSES];

    tableKeys.forEach((tableKey) => {
      const { state } = getTableState(tableKey) || {};
      if (state?.data.some(exp => exp.id === id)) {
        updateTableData({ tableKey, initial: true, query: getInitialQueryParams(tableKey) });
      }
    });

    // If the deleted expense is currently open in the details window, close the details window and reset its props
    setDetailsWindowProps(defaultModalProps);
    handleOpenDetails?.(false);
  }

  const handleInvoiceRemove = ({initialCallback, successCallback, errorCallback} = {}) => {
    transactionsHelpers.removeInvoiceFromTransaction({
      t,
      transaction: {expense: detailsWindowProps?.expense},
      initialCallback,
      successCallback,
      errorCallback,
      unlinkExpense: (props) => dispatch(invoicesActions.unlinkExpense(props)),
      onExpenseUpdate: handleUpdateExpense
    });
  }

  const handleCloseDetailsModal = () => {
    setDetailsWindowProps({...detailsWindowProps, open: false, expense: null, index: null});
    handleOpenDetails?.(false);
  }

  const handleWebsocketMessage = (data) => {
    const getResponseProp = (key) => objectHelpers.getObjProp(data, key);
    const action = getResponseProp('action');

    if (!action) return;

    ({
      [backendWebsocketActionsConstants.EXPENSE_SUBMITTED]: handleSuccessExpenseSubmitted,
      [backendWebsocketActionsConstants.EXPENSE_FILL_MISSING_INFO]: () => {
        const id = getResponseProp('expense_id') || getResponseProp('expense');
        if (!id) return;
        dispatch(expenseActions.getExpense({
          id,
          successCallback: (response) => updateTableAndDetails({id, response, tableKey: detailsWindowProps.tableKey})
        }));
      }
    })[action]?.();
  };

  const handleSubscriptionUpdate = (updatedSubscription) => {
    const subscriptionId = detailsWindowSubscription?.id;
    if (!subscriptionId) return;

    if (updatedSubscription) {
      setSubscriptions(prevState => prevState.map(sub => sub.id === subscriptionId ? {...sub, ...updatedSubscription} : sub));
    }
  }

  const handleSuccessExpenseSubmitted = () => {
    loadTableData({key: LAST_10_EXPENSES, setter: setLastExpensesTableProps, initial: true});
    loadTableData({key: NEED_YOUR_INPUT, setter: setInputTableProps, initial: true});

    if (isOpenAddExpenseModal) {
      dispatch(expenseActions.closeAddExpenseModal());
    }
  }

  const handleSuccessAuth = () => {
    const expense = detailsWindowProps?.expense;
    if (!expense) return;
    loadExpenseTransactions(expense);
  }

  const items = [
    (approvalTableProps.data.length > 0 && isAdmin) && {
      children: getTable({key: NEED_YOUR_APPROVAL, tableProps: approvalTableProps}),
      key: NEED_YOUR_APPROVAL,
      label: getLabel('needYourApproval'),
    },
    inputTableProps.data.length > 0 && {
      children: getTable({key: NEED_YOUR_INPUT, tableProps: inputTableProps}),
      key: NEED_YOUR_INPUT,
      label: getLabel('needYourInput'),
    },
    lastExpensesTableProps.data.length > 0 && {
      children: getTable({key: LAST_10_EXPENSES, tableProps: lastExpensesTableProps, showSeeMoreButton: false}),
      key: LAST_10_EXPENSES,
      label: getLabel('last10Expenses'),
    }
  ].filter(Boolean);

  return (
    <Spin size='small' spinning={isEnableLoading}>
      <StyledExpensesContainer>

        <StyledExpenses ghost items={items} defaultActiveKey={Object.values(tableKeys)} {...rest} />

        {isEmpty && (
          <StyledExpensesEmpty
            image={<EmptySvgImage />}
            description={t('overviewPage.expenses.empty.description')}
            title={t('overviewPage.expenses.empty.title')}
          />
        )}

        <RightSideDrawer open={detailsWindowProps.open} top={90} boxShadow='none'>
          <ExpenseDetailsPanel
            {...detailsWindowProps}
            className='expense-details-panel'
            defaultActivePanelKey={['expense']}
            onApprove={handleApproveDetailsExpense}
            onClose={handleCloseDetailsModal}
            onDelete={handleDeleteExpense}
            onDiscardAutoApproval={handleDiscardAutoApproval}
            onEdit={handleEditExpense}
            onInvoiceRemove={handleInvoiceRemove}
            onSubscriptionUpdate={handleSubscriptionUpdate}
            onUpdate={handleUpdateExpense}
            onReassign={handleReassignExpense}
            onReject={({e, data = {}}) => handleOpenRejectModal({data, e, expense: detailsWindowProps.expense, tableKey: detailsWindowProps.tableKey})}
            onReviewEdit={handleReviewEdit}
            subscription={detailsWindowSubscription}
            transaction={detailsWindowTransaction}
          />
        </RightSideDrawer>

        <RejectExpenseModal
          {...rejectModalProps}
          handleCancel={handleCloseRejectModal}
          handleOk={handleOkRejectModal}
        />

        <ApproveExpenseModal
          {...approveModalProps}
          handleCancel={handleCloseApproveModal}
          handleOk={handleOkApproveModal}
        />

        <ExpenseAgentChat />

        <AddExpenseModal skipClosingAfterSuccess={true} />

        <ExpensesWebsocketConnection onMessage={handleWebsocketMessage} />

        <AuthenticationWindow
          {...authWindowProps}
          authModalProps={{ title: `${t('transaction')} ${t('loading')}` }}
          handleCancel={handleCloseAuthModal}
          onSuccess={handleSuccessAuth}
          operationName={SCAActionsConstants.TRANSACTIONS_WITHIN_90_DAYS_LOADING_ACTION}
        />

      </StyledExpensesContainer>
    </Spin>
  );
}

Expenses.propTypes = {
  handleOpenDetails: PropTypes.func,
}

export default Expenses;
